import React, { FC, useEffect, useState } from 'react';
import { Button, Input, SideModal, Typography } from 'design_system/src';
import { toasterOptions } from 'models/shared/enums/toaster.enum';
import { commonErrorHandler } from 'models/shared/helpers/errors';
import { toasterNotification } from 'models/shared/helpers/toasterNotification';
import { updateWhitelistEntry } from 'models/whitelist/controllers/whitelist.controller';
import {
	WhitelistEntry,
	WhitelistNameEnum,
	WhitelistNameValues,
	WhitelistTypeEnum,
} from 'models/whitelist/interfaces/whitelist.interface';
import { getRequiredTypeForName } from 'models/whitelist/helpers/whitelist-value.helpers';

const getNameOptionsForType = (type?: WhitelistTypeEnum) =>
	Object.values(WhitelistNameEnum)
		.filter((value) => {
			const requiredType = getRequiredTypeForName(value);
			return !requiredType || !type || requiredType === type;
		})
		.map((value) => ({
			value,
			label: WhitelistNameValues[value],
		}));

const activeOptions = [
	{ value: 'true', label: 'Activo' },
	{ value: 'false', label: 'Inactivo' },
];

interface EditWhitelistModalProps {
	open: boolean;
	setOpen: (open: boolean) => void;
	entry: WhitelistEntry | null;
	onUpdated: () => void;
}

const EditWhitelistModal: FC<EditWhitelistModalProps> = ({
	open,
	setOpen,
	entry,
	onUpdated,
}) => {
	const [name, setName] = useState<WhitelistNameEnum>(
		WhitelistNameEnum.abandonedCart,
	);
	const [isActive, setIsActive] = useState('true');
	const [expiresAt, setExpiresAt] = useState('');
	const [saving, setSaving] = useState(false);

	useEffect(() => {
		if (entry) {
			setName(entry.name);
			setIsActive(entry.isActive ? 'true' : 'false');
			setExpiresAt(entry.expiresAt ? entry.expiresAt.slice(0, 10) : '');
		}
	}, [entry]);

	const handleClose = () => {
		setOpen(false);
	};

	const handleSubmit = () => {
		if (!entry) return;

		setSaving(true);
		updateWhitelistEntry(entry._id, {
			name,
			isActive: isActive === 'true',
			expiresAt: expiresAt || null,
		})
			.then(() => {
				toasterNotification({
					msg: 'Valor actualizado exitosamente',
					style: 'light',
					toasterType: toasterOptions.success,
				});
				onUpdated();
				handleClose();
			})
			.catch(commonErrorHandler)
			.finally(() => setSaving(false));
	};

	return (
		<SideModal open={open} setOpen={handleClose} size="small">
			<div className="flex_content_col">
				<Typography
					scale="heading4"
					weight="600"
					textColor="primary_300"
					className="text_center flex_header p_b_md"
				>
					Editar valor de lista blanca
				</Typography>
				<div className="flex_body p_x_md">
					<Typography
						scale="small"
						weight="600"
						textColor="neutral_700"
						className="m_b_md"
					>
						{entry?.value}
					</Typography>

					<Input
						title="Nombre de la lista blanca"
						name="name"
						type="select"
						className="m_b_md w_100_per"
						options={getNameOptionsForType(entry?.type)}
						value={name}
						onChange={(e: any) => setName(e.target.value)}
					/>

					<Input
						title="Estado"
						name="isActive"
						type="select"
						className="m_b_md w_100_per"
						options={activeOptions}
						value={isActive}
						onChange={(e: any) => setIsActive(e.target.value)}
					/>

					<div className="display_flex flex_gap_sm flex_align_center m_b_md w_100_per">
						<Input
							title="Fecha de expiración (opcional)"
							name="expiresAt"
							type="date"
							className="flex_grow_1"
							value={expiresAt}
							onChange={(e: any) => setExpiresAt(e.target.value)}
							subtitle="Si la dejás vacía, el valor no expira"
						/>
						{expiresAt && (
							<Button
								scale="small"
								color="red"
								variant="ghost"
								onClick={() => setExpiresAt('')}
							>
								Quitar fecha
							</Button>
						)}
					</div>

					<Button
						scale="small"
						variant="principal"
						className="w_100_per"
						disabled={saving}
						onClick={handleSubmit}
					>
						{saving ? 'Guardando...' : 'Guardar'}
					</Button>
				</div>
			</div>
		</SideModal>
	);
};

export default EditWhitelistModal;
